import { api } from '../../core/network/api';

/**
 * Thin wrappers around the battle-related REST endpoints. No game logic
 * lives here — these functions only shape requests/responses; the server is
 * the sole authority on combat resolution and on march-time computation.
 *
 * The attack flow is march-then-resolve: `previewAttack` is a non-mutating
 * probe (call it on every army-composition change), `sendArmy` commits the
 * march (units are reserved out of the roster for its duration, a PENDING
 * battle row is created), and the outcome only exists once the march's
 * `arrivalAt` has passed — surfaced later via `fetchBattleHistory`, which
 * lazily resolves the caller's own overdue battles on read.
 *
 * See battle-scene.ts for the playback-of-a-decided-outcome visualization
 * that was built for the old instant-resolve flow — it's dormant now (no
 * outcome is ever returned synchronously anymore) but left in place for a
 * possible future formation/visual-battle feature.
 */

export type UnitType =
  | 'LIGHT_INFANTRY'
  | 'DEFENCE_BUNKER'
  | 'ATTACK_ROVER'
  | 'ATTACK_GUNSHIP'
  | 'SCOUT_BIKE'
  | 'DEFENCE_TURRET_WALKER'
  | 'SIEGE_ARTILLERY'
  | 'SIEGE_MORTAR_TANK'
  | 'SETTLER_CONVOY';

export type ResourceType = 'REGOLITH' | 'WATER_ICE' | 'HE3' | 'COMPOSITE' | 'CRIMSON_DUST';

export interface MyColony {
  id: string;
}

export interface RosterEntry {
  unitType: UnitType;
  level: number;
  quantityReserved: number;
  training: boolean;
  locked: boolean;
  /** Crew occupied per unit — the number a formation's crew cap (see PreviewResult) is computed from. */
  crewSize: number;
}

/** March orders — mirrors backend battle.formations.ts. */
export type FormationId = 'BALANCED' | 'FORCED_MARCH' | 'ASSAULT' | 'PLUNDER';

/** One formation's modifiers (GET /me/battle/formations). Factors are multipliers; 1 = unchanged. The server owns the numbers. */
export interface FormationDef {
  id: FormationId;
  marchTimeFactor: number;
  attackFactor: number;
  carryFactor: number;
  /** Fraction of each stockpile the force tries to lift. */
  lootShare: number;
}

export interface AttackUnitInput {
  unitType: UnitType;
  quantity: number;
}

/**
 * Non-mutating preview of YOUR OWN force and its journey. It reveals nothing
 * about the target: the old win/even/loss band was removed because repeated
 * free calls could bisect it to recover the defender's exact power (see the
 * backend's previewAttack). Knowing what you are walking into is earned by
 * sending recon or by fighting.
 */
export interface PreviewResult {
  attackerPower: number;
  distanceTiles: number;
  marchSeconds: number;
  /** The slowest unit in the chosen force — the one setting the march's pace. An army moves at its slowest member's speed. */
  paceUnitType: UnitType;
  paceMinutesPerTile: number;
  /** Total crew this composition occupies, and the attacker's cap at their current Barracks level. */
  crewUsed: number;
  crewCapacity: number;
  /** Upper bound on plunder — the full force's carry capacity before any losses; the actual haul is capped by survivors. */
  carryCapacity: number;
  /** The formation these numbers were computed for. Differs from the one requested when the server ignored it (recon-only force). */
  formation: FormationId;
}

/** Mutating: reserves the chosen units for the whole march and creates a PENDING battle. No outcome — the fight hasn't happened yet. */
export interface SendArmyResult {
  battleId: string;
  defenderColonyId: string;
  distanceTiles: number;
  marchSeconds: number;
  startedAt: string;
  arrivalAt: string;
  /** The slowest unit in the force, which set this march's pace. */
  paceUnitType: UnitType;
  paceMinutesPerTile: number;
  formation: FormationId;
}

/** One side's livery for a resolved battle, as resolved by the server. */
export interface ResolvedSideColor {
  colonyId: string;
  colorId: string;
  hex: string;
  label: string;
  /** True when this side was moved off its primary because both sides picked the same one. */
  usedSecondary: boolean;
}

export interface ResolvedBattleColors {
  attacker: ResolvedSideColor;
  defender: ResolvedSideColor;
  clashResolved: boolean;
}

export type ReconIntelTier = 'NONE' | 'BUILDINGS' | 'UNIT_COUNTS' | 'UNIT_COUNTS_AND_TYPES' | 'FULL';

export interface BattleIntel {
  tier: ReconIntelTier;
  buildings?: { type: string; level: number }[];
  totalUnitCount?: number;
  unitCounts?: Partial<Record<UnitType, number>>;
  resources?: Partial<Record<ResourceType, number>>;
}

export interface BattleResultSummary {
  /** The march order the attacker used. Absent on older rows and on Balanced marches. */
  formation?: FormationId;
  attackerWins: boolean;
  attackerPower: number;
  defenderPower: number;
  plunder: Partial<Record<ResourceType, number>>;
  /** True when the surviving force's carry capacity, not the 20%/headroom rule, was the binding ceiling on plunder. Optional — older persisted rows predate this field. */
  plunderCapped?: boolean;
  /** What the attacker marched with, per unit type. Optional — rows from before this field existed lack it. */
  attackerSent?: Partial<Record<UnitType, number>>;
  attackerLosses: Partial<Record<UnitType, number>>;
  defenderLosses: Partial<Record<UnitType, number>>;
  /** Defender's surviving garrison per defending unit type, post-battle — see backend battle.types.ts for why this is safe to reveal post-resolution. */
  defenderRemaining: Partial<Record<UnitType, number>>;
  colors: ResolvedBattleColors;
  /** True when this march was a pure recon mission — render a Recon Report instead of a casualty/plunder table. */
  isRecon?: boolean;
  /** Intel earned by a recon mission, or as a side-benefit of an ordinary assault. */
  intel?: BattleIntel;
  captured?: boolean;
  captureBlockedByColonyCap?: boolean;
}

/**
 * Alias kept for battle-scene.ts, which is left untouched per the design
 * brief (dormant since the instant-resolve flow was removed, but staged for
 * a possible future formation/visual-battle feature) — it still imports the
 * old `BattleResult` name.
 */
export type BattleResult = BattleResultSummary;

export interface BattleHistoryEntry {
  id: string;
  attackerId: string;
  attackerColonyName: string;
  attackerUsername: string | null;
  /** The attacker's equipped cosmetic title, resolved to its display label by the server. */
  attackerTitle?: string | null;
  defenderId: string;
  defenderColonyName: string;
  defenderUsername: string | null;
  /** The defender's equipped cosmetic title, resolved to its display label by the server. */
  defenderTitle?: string | null;
  /** RETURNING means combat is decided and resultSummary is populated — survivors are just still walking home. Rendered identically to RESOLVED. */
  status: 'PENDING' | 'RETURNING' | 'RESOLVED' | 'CANCELLED';
  startedAt: string;
  arrivalAt: string;
  resolvedAt: string | null;
  distanceTiles: number;
  marchSeconds: number;
  /** The attacker's march order (Balanced on older rows). */
  formation?: FormationId;
  defenderPosition: { x: number; y: number } | null;
  resultSummary: BattleResultSummary | null;
}

/** The caller's own colony — only `id` is used here. */
export async function fetchMyColonyId(): Promise<string> {
  const colony = await api.get<MyColony>('/me/colony');
  return colony.id;
}

/** The caller's full unit roster (including locked/in-training entries; filter client-side as needed). */
export async function fetchRoster(): Promise<RosterEntry[]> {
  return api.get<RosterEntry[]>('/me/roster');
}

/** Non-mutating — safe to call on every composition edit. */
export async function previewAttack(
  defenderColonyId: string,
  units: AttackUnitInput[],
  formation: FormationId = 'BALANCED',
): Promise<PreviewResult> {
  return api.post<PreviewResult>('/me/battle/preview', { defenderColonyId, units, formation });
}

/** The selectable formations and their modifiers. */
export async function fetchFormations(): Promise<FormationDef[]> {
  return api.get<FormationDef[]>('/me/battle/formations');
}

/** Mutating — commits the march. Reserves units out of the roster; no outcome is returned. */
export async function sendArmy(
  defenderColonyId: string,
  units: AttackUnitInput[],
  formation: FormationId = 'BALANCED',
): Promise<SendArmyResult> {
  return api.post<SendArmyResult>('/me/battle/send', { defenderColonyId, units, formation });
}

/** The caller's past + in-flight battles (as attacker or defender), newest first. Lazily resolves any overdue PENDING battles of the caller's own on read. */
export async function fetchBattleHistory(
  limit = 20,
  offset = 0,
): Promise<BattleHistoryEntry[]> {
  return api.get<BattleHistoryEntry[]>(`/me/battle/history?limit=${limit}&offset=${offset}`);
}

/** Recalls an in-flight march — only the attacker can cancel, and only before the last 10% of travel time (see backend battle.constants.ts's MARCH_CANCEL_CUTOFF_FRACTION). Units sent return to the roster untouched. */
export async function cancelBattle(battleId: string): Promise<unknown> {
  return api.post<unknown>(`/me/battle/${battleId}/cancel`);
}
